import { useSyncExternalStore } from 'react';

// Module-level singleton — shared across all hook consumers
let contentRef = null;
let state = { isLoading: false };
const listeners = new Set();

const setState = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => state;

export const setPdfContentRef = (ref) => {
  contentRef = ref;
};

const PDF_WIDTH = 1440;

const getViewportMeta = () => document.querySelector('meta[name="viewport"]');

// Force the page to render at PDF_WIDTH so both regular pages and iframes
// (including mobile) produce a consistent desktop layout in the PDF.
const forceWidth = () => {
  const meta = getViewportMeta();
  if (meta) meta.content = `width=${PDF_WIDTH}, initial-scale=1`;
  document.documentElement.style.minWidth = `${PDF_WIDTH}px`;
  document.body.classList.add('pdf-rendering');
  document.documentElement.style.overflow = 'hidden';
  document.body.style.overflow = 'hidden';
  window.dispatchEvent(new Event('resize'));
};

const restoreWidth = (originalMeta, originalMinWidth) => {
  const meta = getViewportMeta();
  if (meta) meta.content = originalMeta;
  document.documentElement.style.minWidth = originalMinWidth;
  document.documentElement.style.overflow = '';
  document.body.style.overflow = '';
  document.body.classList.remove('pdf-rendering');
  window.dispatchEvent(new Event('resize'));
};

const usePdfDownload = () => {
  const { isLoading } = useSyncExternalStore(subscribe, getSnapshot);

  const downloadPdf = async () => {
    if (!contentRef?.current) return;

    const originalMeta = getViewportMeta()?.content ?? '';
    const originalMinWidth = document.documentElement.style.minWidth;

    setState({ isLoading: true });

    // Wait one frame so the overlay renders before the viewport changes
    await new Promise((resolve) => requestAnimationFrame(resolve));
    forceWidth();

    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
        // Wait for layout and charts to redraw at the new width
        new Promise((resolve) => setTimeout(resolve, 600))
      ]);

      const scale = 2;
      const canvas = await html2canvas(contentRef.current, {
        scale,
        useCORS: true,
        allowTaint: true,
        logging: false,
        imageTimeout: 0,
        // Keep the clone's media queries in sync with the forced viewport width
        windowWidth: PDF_WIDTH,
        onclone: (_doc, element) => {
          element
            .querySelectorAll('[data-pdf-exclude]')
            .forEach((el) => el.remove());
        }
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.99);
      const pdfWidth = canvas.width / scale;
      const pdfHeight = canvas.height / scale;

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [pdfWidth, pdfHeight]
      });

      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      pdf.save('statistics.pdf');
    } finally {
      restoreWidth(originalMeta, originalMinWidth);
      setState({ isLoading: false });
    }
  };

  return { downloadPdf, isLoading };
};

export default usePdfDownload;
